export const THEME_NAMES = ['maison', 'noir', 'atelier', 'bloom', 'avenue', 'jardin', 'claude-design-v1'] as const;
export type ThemeName = typeof THEME_NAMES[number];

/**
 * Identifiers for every section that can appear on the homepage.
 * Each theme picks a subset and orders them. The home page renders
 * sections in the order specified by the active theme's `sections`
 * array — themes that don't list a section simply skip it.
 *
 * Adding a new section: add the identifier here, render it in the
 * switch in `app/[locale]/page.tsx`, then add it to whichever themes
 * should show it.
 */
export type HomeSection =
  | 'hero'
  | 'trust'
  | 'categories'
  | 'services'
  | 'featured'
  | 'builderCta'
  | 'process'
  | 'testimonials'
  | 'instagram'
  | 'newsletter';

export interface ThemeLayout {
  heroStyle: 'fullscreen' | 'split' | 'contained';
  cardStyle: 'flat' | 'elevated' | 'bordered' | 'glass';
  headerStyle: 'transparent' | 'solid' | 'floating';
  productGrid: '3-col' | '4-col' | 'masonry';
  buttonStyle: 'sharp' | 'rounded' | 'pill';
  borderRadius: 'none' | 'sm' | 'md' | 'lg' | 'full';
  spacing: 'compact' | 'normal' | 'spacious';
}

export interface ThemeDefinition {
  name: ThemeName;
  label: string;
  description: string;
  preview: { primary: string; secondary: string; accent: string; background: string; };
  fonts: { display: string; body: string; };
  layout: ThemeLayout;
  /**
   * Ordered list of home sections this theme renders. Themes can
   * differ in BOTH composition (which sections appear) and order
   * (which section comes first/last). Default fallback if a theme
   * omits the field is `DEFAULT_HOME_SECTIONS`.
   */
  sections: HomeSection[];
}

/**
 * Default home composition — all 10 sections in the original hardcoded
 * order. Used as a fallback if a theme definition forgets to set
 * `sections`. Each theme overrides this with its own composition.
 */
export const DEFAULT_HOME_SECTIONS: HomeSection[] = [
  'hero',
  'trust',
  'categories',
  'services',
  'featured',
  'builderCta',
  'process',
  'testimonials',
  'instagram',
  'newsletter',
];

export const themes: Record<ThemeName, ThemeDefinition> = {
  // ─── MAISON — French haute couture, lush brand-led composition ───
  // Full storytelling: hero → trust → categories → featured → process →
  // testimonials → instagram → newsletter. Skips services + builderCta
  // because the brand voice is "ready-to-wear luxury", not made-to-order.
  maison: {
    name: 'maison',
    label: 'Maison',
    description: 'French haute couture — ivory, charcoal, gold accents with Playfair Display. Full lush brand-storytelling composition.',
    preview: { primary: '#C5A258', secondary: '#2C2C2C', accent: '#C5A258', background: '#FDFBF7' },
    fonts: { display: 'Playfair Display', body: 'Assistant' },
    layout: { heroStyle: 'fullscreen', cardStyle: 'flat', headerStyle: 'transparent', productGrid: '4-col', buttonStyle: 'rounded', borderRadius: 'md', spacing: 'spacious' },
    sections: ['hero', 'trust', 'categories', 'featured', 'process', 'testimonials', 'instagram', 'newsletter'],
  },
  // ─── NOIR — Dark editorial, minimalist, products-first ───
  // 4 sections only. No trust badges, no process steps, no testimonials,
  // no instagram. The brand voice is "let the products speak". Heavy
  // contrast, dramatic, concise.
  noir: {
    name: 'noir',
    label: 'Noir',
    description: 'Dark editorial luxury — black backgrounds, champagne gold. Minimal 4-section composition that lets the products speak.',
    preview: { primary: '#C9A96E', secondary: '#0A0A0A', accent: '#C9A96E', background: '#0A0A0A' },
    fonts: { display: 'Cormorant Garamond', body: 'Raleway' },
    layout: { heroStyle: 'fullscreen', cardStyle: 'glass', headerStyle: 'transparent', productGrid: '3-col', buttonStyle: 'sharp', borderRadius: 'sm', spacing: 'spacious' },
    sections: ['hero', 'featured', 'categories', 'newsletter'],
  },
  // ─── ATELIER — Warm artisan studio, process-forward ───
  // Process steps come BEFORE the categories — the brand voice is
  // "see how it's made, then shop". Trust badges high, testimonials
  // for craft credibility.
  atelier: {
    name: 'atelier',
    label: 'Atelier',
    description: 'Warm artisan studio — cream, terracotta, espresso. Process-forward composition: see how it\'s made, then shop.',
    preview: { primary: '#B87333', secondary: '#3E2723', accent: '#B87333', background: '#FAF5EF' },
    fonts: { display: 'DM Serif Display', body: 'Work Sans' },
    layout: { heroStyle: 'split', cardStyle: 'bordered', headerStyle: 'solid', productGrid: '3-col', buttonStyle: 'rounded', borderRadius: 'md', spacing: 'normal' },
    sections: ['hero', 'trust', 'process', 'categories', 'featured', 'testimonials', 'newsletter'],
  },
  // ─── BLOOM — Soft feminine, romantic, social-proof heavy ───
  // Testimonials AND instagram both prominent. Builder CTA included
  // because Bloom audiences love "make it yours". Process near the end.
  bloom: {
    name: 'bloom',
    label: 'Bloom',
    description: 'Soft feminine elegance — blush pink, mauve, plum. Social-proof-heavy composition with testimonials, instagram, and "make it yours" customization.',
    preview: { primary: '#C48B9F', secondary: '#4A2040', accent: '#C48B9F', background: '#FDF5F7' },
    fonts: { display: 'Cormorant Garamond', body: 'Quicksand' },
    layout: { heroStyle: 'contained', cardStyle: 'elevated', headerStyle: 'solid', productGrid: '4-col', buttonStyle: 'pill', borderRadius: 'lg', spacing: 'normal' },
    sections: ['hero', 'categories', 'featured', 'testimonials', 'instagram', 'builderCta', 'process', 'newsletter'],
  },
  // ─── AVENUE — Clean architectural, products-first, minimal ───
  // 5 sections. Sharp, monochrome, no fluff. Builder CTA included
  // (architectural audience likes precision/customization). No
  // testimonials, no instagram, no process.
  avenue: {
    name: 'avenue',
    label: 'Avenue',
    description: 'Clean metropolitan luxury — monochrome, architectural. Tight 5-section composition for the product-first shopper.',
    preview: { primary: '#1A1A1A', secondary: '#F5F5F5', accent: '#333333', background: '#FFFFFF' },
    fonts: { display: 'Raleway', body: 'Inter' },
    layout: { heroStyle: 'contained', cardStyle: 'flat', headerStyle: 'solid', productGrid: '4-col', buttonStyle: 'sharp', borderRadius: 'none', spacing: 'compact' },
    sections: ['hero', 'featured', 'categories', 'builderCta', 'newsletter'],
  },
  // ─── JARDIN — Mediterranean garden, serene, services-forward ───
  // Services section appears HIGH in the order (right after trust).
  // This is the only theme that surfaces the salon services prominently
  // — the brand voice is "we're a salon experience, not just a shop".
  // Slow, full composition.
  jardin: {
    name: 'jardin',
    label: 'Jardin',
    description: 'Mediterranean garden — sage green, ivory, warm terracotta. Services-forward composition that surfaces the salon experience above the catalog.',
    preview: { primary: '#7D8C6E', secondary: '#3D4A35', accent: '#C4956A', background: '#F8F6F0' },
    fonts: { display: 'DM Serif Display', body: 'Lato' },
    layout: { heroStyle: 'split', cardStyle: 'elevated', headerStyle: 'floating', productGrid: '3-col', buttonStyle: 'rounded', borderRadius: 'md', spacing: 'spacious' },
    sections: ['hero', 'trust', 'services', 'categories', 'featured', 'process', 'testimonials', 'newsletter'],
  },
  // ─── CLAUDE DESIGN V1 — editorial refinement from Claude Design ───
  // Output of the Claude Design (Anthropic Labs) handoff. A refinement
  // of Maison, not a reinvention: same cream/gold/charcoal + Playfair/
  // Assistant, with tightened tokens (warm charcoal dark sections,
  // AA-safe gold-dark, crisper radii, soft warm-tinted shadows) and a
  // gateway-card homepage. Composition mirrors project/index.html:
  // gateway hero → light trust strip → testimonial → instagram journey.
  // The bespoke homepage layout renders only under this theme (see
  // app/[locale]/page.tsx); other themes are untouched.
  'claude-design-v1': {
    name: 'claude-design-v1',
    label: 'Claude Design v1',
    description: 'Editorial refinement from Claude Design — warm ivory, AA-safe gold, warm-charcoal dark sections, Playfair italic gateway. Gateway-card homepage composition.',
    preview: { primary: '#C5A258', secondary: '#16140F', accent: '#C5A258', background: '#FDFBF7' },
    fonts: { display: 'Playfair Display', body: 'Assistant' },
    layout: { heroStyle: 'split', cardStyle: 'bordered', headerStyle: 'floating', productGrid: '4-col', buttonStyle: 'sharp', borderRadius: 'sm', spacing: 'spacious' },
    sections: ['hero', 'trust', 'testimonials', 'instagram'],
  },
};

export function getThemeByName(name: string): ThemeDefinition {
  return themes[name as ThemeName] ?? themes.maison;
}

/**
 * Server-side helper that resolves the currently-active theme,
 * including the admin "preview-theme" cookie override. Used by both
 * the locale layout (to seed ThemeProvider) and the home page (to
 * decide which sections to render).
 *
 * Reads `prisma.siteSettings` once per request — Next.js dedupes the
 * fetch within a single render, so calling this multiple times on the
 * same page only hits the DB once. Falls back to 'maison' on any
 * error so the page never breaks.
 *
 * Note: NOT cached with `unstable_cache` because the preview cookie
 * is request-scoped — caching would leak previews across users.
 */
export async function getActiveTheme(): Promise<ThemeDefinition> {
  // Lazy imports so this file stays usable from client components
  // (which don't have access to next/headers or prisma).
  const { prisma } = await import('@/lib/prisma');
  const { cookies } = await import('next/headers');

  let themeName: string = 'maison';
  try {
    const settings = await prisma.siteSettings.findFirst({ where: { id: 'singleton' } });
    if (settings?.theme) themeName = settings.theme;
  } catch {
    /* default to maison */
  }
  try {
    const cookieStore = await cookies();
    const preview = cookieStore.get('preview-theme')?.value;
    if (preview && THEME_NAMES.includes(preview as ThemeName)) {
      themeName = preview;
    }
  } catch {
    /* ignore */
  }
  return getThemeByName(themeName);
}
